import { intersection } from 'lodash';
import React, { ReactNode, useCallback } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { useQueryClient } from 'react-query';
import { PermissionSetType, ProjectId, User, UserType } from 'api/models';
import { ProjectAPI, ProjectUserLiteMembership } from 'api/project';
import { confirm } from 'components/atoms/modal';
import { SquadValue } from 'components/molecules/squad-picker';
import { Option } from 'components/molecules/squad-picker/types';
import { useProjectSquad } from 'components/molecules/squad-picker/use-squad-options';
import {
  filterUserOptions,
  optionsToSquadPayload,
  teamTypeOption,
} from 'components/molecules/squad-picker/utils';
import { AssigneeValue } from 'components/organisms/tasks/fields';
import { InvitedMembers } from 'data/members/slice';
import { useAddMembersToProjectMutation } from 'queries';
import { getProjectUsersLiteKey } from 'queries/common';
import { useUsersQuery } from 'queries/members';
import { useProjectUsersLiteForProjectQuery } from 'queries/project';
import { logger } from 'services';
import { TranslationKey } from './use-translation';

type UserId = number;

export const filterNotInProjectUsers = (
  selected: UserId[],
  notInProjectUsers: User[],
): User[] => {
  return notInProjectUsers.filter(({ userId }) => selected.includes(userId));
};

const toInviteUsersPayload = (
  users: Pick<User, 'userId' | 'userType'>[],
): InvitedMembers[] => {
  return users.map(({ userId, userType }) => ({
    user: { userId },
    permissionSet: {
      permissionSetType:
        userType === UserType.External
          ? PermissionSetType.CustomerCollaborator
          : userType === UserType.Partner
          ? PermissionSetType.PartnerCollaborator
          : PermissionSetType.Collaborator,
    },
  }));
};

type BaseVariables = {
  value: UserId[];
  notInProjectUsersSource: User[];
};

interface Configurations extends BaseVariables {
  projectId: ProjectId;
  intl: IntlShape;
}

type Variables = Configurations;

type ConfirmModalTextKeys = {
  title: TranslationKey;
  content: TranslationKey;
  okText: TranslationKey;
  cancelText: TranslationKey;
};

type ConfirmModalText = {
  title?: string;
  content?: string;
  okText?: string;
  cancelText?: string;
};

type GetConfirmModalTextsParams = {
  intl: IntlShape;
  confirmModalTextKeys?: Partial<ConfirmModalTextKeys>;
  confirmModalText?: Partial<ConfirmModalText>;
  notInProjectUsers: User[];
  project?: ReactNode;
};

const getConfirmModalTexts = ({
  intl,
  confirmModalTextKeys = {},
  confirmModalText = {},
  notInProjectUsers,
  project,
}: GetConfirmModalTextsParams) => {
  const {
    title = 'invitation.team_member.confirm.title',
    content = 'invitation.via.general.confirm.content',
    okText = 'continue',
    cancelText = 'cancel',
  } = confirmModalTextKeys;

  return {
    title: confirmModalText.title || intl.formatMessage({ id: title }),
    content:
      confirmModalText.content ||
      intl.formatMessage(
        { id: content },
        {
          count: notInProjectUsers.length,
          emailIds: (
            <b>{notInProjectUsers.map(({ emailId }) => emailId).join(', ')} </b>
          ),
          project:
            project ??
            intl.formatMessage({
              id: 'invitation.via.general.confirm.content.default_project',
            }),
        },
      ),
    okText: confirmModalText.okText || intl.formatMessage({ id: okText }),
    cancelText:
      confirmModalText.cancelText || intl.formatMessage({ id: cancelText }),
  };
};

export interface GetUsersParams extends Variables {
  onOk?: (result: User[], variables: GetUsersParams) => Promise<any>;
  confirmModalTextKeys?: Partial<ConfirmModalTextKeys>;
  confirmModalText?: Partial<ConfirmModalText>;
  project?: ReactNode;
}

export const getUsers = (params: GetUsersParams): Promise<UserId[]> => {
  const {
    value,
    notInProjectUsersSource,
    onOk,
    confirmModalTextKeys = {},
    confirmModalText = {},
    intl,
    project,
  } = params;

  return new Promise<UserId[]>((resolve) => {
    const ids = value;
    const result = filterNotInProjectUsers(ids, notInProjectUsersSource);
    const notInProjectUserIds = result.map(({ userId }) => userId);
    const inProjectUserIds = ids.filter(
      (id) => !notInProjectUserIds.includes(id),
    );

    if (!notInProjectUserIds.length) {
      return resolve(value);
    }
    const { title, cancelText, okText, content } = getConfirmModalTexts({
      intl,
      notInProjectUsers: result,
      confirmModalTextKeys,
      confirmModalText,
      project,
    });

    return confirm({
      title,
      content,
      cancelText,
      okText,
      onOk: async () => {
        try {
          await onOk?.(result, params);

          return resolve(ids);
        } catch (error) {
          logger.error('Invite Request failed', { error });

          return resolve(inProjectUserIds);
        }
      },
      onCancel: () => resolve(inProjectUserIds),
    });
  });
};

export const useAddMembersToProject = () => {
  const { mutateAsync } = useAddMembersToProjectMutation();

  return (
    result: User[],
    {
      projectId,
      suppressToast,
    }: Pick<GetUsersParams, 'projectId'> & { suppressToast?: boolean },
  ) => {
    return mutateAsync({
      projectId,
      suppressToast,
      payload: toInviteUsersPayload(result),
    });
  };
};

const useUserInvitationPopup = (
  projectId: ProjectId,
  configOptions?: Pick<
    GetUsersParams,
    'confirmModalTextKeys' | 'confirmModalText'
  > & { enabled?: boolean },
  onOk?: (result: User[], variables: GetUsersParams) => Promise<any>,
): ((selected: UserId[]) => Promise<UserId[]>) => {
  const {
    data: { notInProject },
  } = useProjectSquad(projectId);
  const intl = useIntl();

  return useCallback(
    async (value) => {
      return getUsers({
        value,
        intl,
        onOk,
        projectId,
        notInProjectUsersSource: notInProject,
        ...configOptions,
      });
    },
    [notInProject, intl, onOk, projectId, configOptions],
  );
};
const useInviteUsersToProjectInDefaultRole = () => {
  const queryClient = useQueryClient();
  const { data: allUsers } = useUsersQuery();
  const onOk = useAddMembersToProject();

  return useCallback(
    async (selected: UserId[], projectId: number): Promise<UserId[]> => {
      if (!selected.length) {
        return selected;
      }
      const projectUsersLite =
        await queryClient.fetchQuery<ProjectUserLiteMembership>({
          queryKey: getProjectUsersLiteKey(projectId),
          queryFn: () => ProjectAPI.getProjectUsersLiteForProject(projectId),
        });

      const userIdsInOwnRoleInProject = (
        projectUsersLite?.members ?? []
      ).reduce(
        (userIdMap, { user: { userId }, role, referenceTeam }) => ({
          ...userIdMap,
          [userId]: role == null && referenceTeam == null,
        }),
        {},
      );
      const idsNeedToInvitedAgain = selected.filter(
        (id) => !userIdsInOwnRoleInProject[id],
      );

      if (idsNeedToInvitedAgain.length === 0) {
        return selected;
      }

      await onOk(
        idsNeedToInvitedAgain
          .map((id) => allUsers?.find(({ userId }) => userId === id))
          .filter(Boolean) as User[],
        { projectId: ProjectId(projectId), suppressToast: true },
      );

      return selected;
    },
    [allUsers, onOk, queryClient],
  );
};

export const useInviteUserToProject = (
  projectId: ProjectId,
  configOptions?: Pick<
    GetUsersParams,
    'confirmModalTextKeys' | 'confirmModalText'
  > & { enabled?: boolean; addUsersInDefaultRole?: boolean },
): ((selected: UserId[]) => Promise<UserId[]>) => {
  const inviteUsersToDefaultRole = useInviteUsersToProjectInDefaultRole();

  useProjectUsersLiteForProjectQuery(projectId[1]);
  const onOk = useAddMembersToProject();
  const invitationPopupHandler = useUserInvitationPopup(
    projectId,
    configOptions,
    onOk,
  );
  const invitationHandler = useCallback(
    (selected: UserId[], newlyAdded: UserId[] = []) => {
      return invitationPopupHandler(selected).then(
        async (selectedIds: UserId[]) => {
          if (configOptions?.addUsersInDefaultRole !== true) {
            return selectedIds;
          }
          await inviteUsersToDefaultRole(
            intersection(selectedIds, newlyAdded),
            projectId[1],
          );

          return selectedIds;
        },
      );
    },
    [
      configOptions?.addUsersInDefaultRole,
      invitationPopupHandler,
      inviteUsersToDefaultRole,
      projectId,
    ],
  );

  return invitationHandler;
};

export const useInviteUserToProjectPopup = (
  projectId: ProjectId,
  configOptions?: Pick<
    GetUsersParams,
    'confirmModalTextKeys' | 'confirmModalText'
  > & { enabled?: boolean },
): ((selected: UserId[]) => Promise<UserId[]>) =>
  useUserInvitationPopup(projectId, configOptions);

export interface UserIdTransformer<T> {
  from: (value: UserId[], original: T) => T;
  to: (value: T) => UserId[];
}

export const SINGLE_USER_TRANSFORMER: UserIdTransformer<UserId | null> = {
  from: (value: UserId[], original: UserId | null) =>
    original == null ? null : value.includes(original) ? original : null,
  to: (value: UserId | null) => [value].filter(Boolean).map((x) => x as UserId),
};

export const MULTI_USER_TRANSFORMER: UserIdTransformer<UserId[]> = {
  from: (value: UserId[], original: UserId[]) =>
    original.filter((x) => value.includes(x)),
  to: (value: UserId[]) => value,
};

export const ASSIGNEE_TRANSFORMER: UserIdTransformer<SquadValue> = {
  from: (value: UserId[], { users, teams }: SquadValue) => ({
    teams,
    users: users.filter((u) => value.includes(u.userId)),
  }),
  to: (value: SquadValue) => (value?.users ?? []).map(({ userId }) => userId),
};

export const ASSIGNEE_VALUE_TRANSFORMER: UserIdTransformer<AssigneeValue[]> = {
  from: (value: UserId[], assignee_values: AssigneeValue[]) =>
    (assignee_values ?? []).filter(
      ([type, id]) => type === 'team' || value.includes(id),
    ),
  to: (value: AssigneeValue[]) =>
    (value ?? []).filter(([type]) => type === 'user').map(([, id]) => id),
};

type ProjectInvitationHandler<T> = (
  selected: T,
  projectId: ProjectId,
  notInProject: User[],
  project?: ReactNode,
) => Promise<T>;

export const useProjectInvitationHandler = function <T>(
  { from, to }: UserIdTransformer<T>,
  addedUsersGetter: (values: T) => number[],
  configOptions?: Pick<GetUsersParams, 'confirmModalTextKeys'>,
): ProjectInvitationHandler<T> {
  const onOk = useAddMembersToProject();
  const intl = useIntl();
  const inviteUsersToDefaultRole = useInviteUsersToProjectInDefaultRole();

  return useCallback(
    async (
      value: T,
      projectId: ProjectId,
      notInProject: User[],
      project?: ReactNode,
    ) => {
      return getUsers({
        value: to(value),
        intl,
        onOk,
        projectId,
        notInProjectUsersSource: notInProject,
        ...configOptions,
        project,
      })
        .then(async (users) => {
          await inviteUsersToDefaultRole(
            intersection(users, addedUsersGetter(value)),
            projectId[1],
          );

          return users;
        })
        .then((users) => from(users, value));
    },
    [
      to,
      intl,
      onOk,
      configOptions,
      inviteUsersToDefaultRole,
      addedUsersGetter,
      from,
    ],
  );
};

export const useMultiUserSelectValue = (
  projectId: ProjectId,
): ((value: number[] | null) => Promise<number[] | null>) => {
  const handle = useInviteUserToProject(projectId);

  return async (value: number[] | null) => {
    if (value) {
      const result = await handle(value);

      return result;
    }

    return value;
  };
};

const useGetMemberInvitationConfirmation = (
  handler: (selected: UserId[], added: UserId[]) => Promise<UserId[]>,
): ((value: Option[], added?: Option[]) => Promise<Option[]>) =>
  useCallback(
    async (value, added = []) => {
      const source = filterUserOptions(value);
      const userIds = optionsToSquadPayload(source).users.map(
        ({ userId }) => userId,
      );
      const result = await handler(
        userIds,
        optionsToSquadPayload(added).users.map(({ userId }) => userId),
      );
      const teamOpts = value.filter(teamTypeOption);
      const userOpts = source.filter((option) =>
        result.includes(option.extra.id),
      );

      return [...userOpts, ...teamOpts];
    },
    [handler],
  );

export const useAssigneeOptionsSelectValue = (
  projectId: ProjectId,
): ((value: Option[]) => Promise<Option[]>) => {
  const handler = useInviteUserToProject(projectId, {
    confirmModalTextKeys: {
      content: 'invitation.via.assign.confirm.content',
      okText: 'invite_and_assign',
    },
    addUsersInDefaultRole: true,
  });

  return useGetMemberInvitationConfirmation(handler);
};

export const useMemberInvitationConfirmation = (
  projectId: ProjectId,
): ((value: Option[]) => Promise<Option[]>) => {
  const handler = useInviteUserToProjectPopup(projectId, {
    confirmModalTextKeys: {
      content: 'invitation.via.assign.confirm.content',
      okText: 'invite_and_assign',
    },
  });

  return useGetMemberInvitationConfirmation(handler);
};

interface UseSingleUserMentionHandlerOptions {
  enabled?: boolean;
}

export const useSingleUserMentionHandler = (
  projectId: ProjectId,
  options: UseSingleUserMentionHandlerOptions,
): ((value: number | null) => Promise<number | null>) => {
  const handle = useInviteUserToProject(projectId, {
    ...options,
    confirmModalTextKeys: {
      content: 'invitation.via.mention.confirm.content',
      okText: 'invite_and_mention',
    },
  });

  return useCallback(
    async (value: number | null) => {
      if (value) {
        const result = await handle([value]);

        return result ? (result.length > 0 ? result[0] : null) : null;
      }

      return value;
    },
    [handle],
  );
};
